/**
 * Two pronunciation sources, picked in Settings:
 *
 * - `browser` — Web Speech API. Offline, instant, but the voice is whatever
 *   the device ships with.
 * - `youdao`  — streamed recordings from the Youdao dictionary, one per
 *   accent (UK / US). Better quality, needs the network.
 *
 * Both fail silently rather than throwing: online playback falls back to the
 * browser voice, and a browser without speech synthesis just does nothing.
 */

export type Accent = "uk" | "us";

/** Youdao's `type` param: 1 = British, 2 = American. */
function youdaoUrl(word: string, accent: Accent): string {
  const type = accent === "uk" ? 1 : 2;
  return `https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(word)}&type=${type}`;
}

function accentLang(accent: Accent): string {
  return accent === "uk" ? "en-GB" : "en-US";
}

/**
 * Lightweight Web Speech API wrapper. Falls back silently when the browser
 * does not support speech synthesis (e.g., embedded views, some mobile
 * browsers) — the AudioButton remains visible but inactive.
 */
export function speak(text: string, lang = "en-US"): void {
  if (typeof window === "undefined") return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang;
    utter.rate = 0.95;
    synth.speak(utter);
  } catch {
    /* ignore */
  }
}

/** Only one clip plays at a time; a new tap cuts the previous one short. */
let current: { audio: HTMLAudioElement; cut: () => void } | null = null;

/**
 * Stream the dictionary recording for `word` in the given accent. Resolves
 * when playback ends, fails, or is cut short by a newer tap — so callers can
 * hold a "playing" state for exactly as long as sound is coming out. Failures
 * (offline, blocked autoplay, or an entry Youdao has no recording for) fall
 * back to the browser voice.
 */
export async function playOnline(word: string, accent: Accent): Promise<void> {
  if (typeof window === "undefined") return;
  window.speechSynthesis?.cancel();
  current?.cut();

  const audio = new Audio(youdaoUrl(word, accent));
  let settle!: (outcome: "ended" | "failed" | "cut") => void;
  const finished = new Promise<"ended" | "failed" | "cut">((resolve) => {
    settle = resolve;
  });
  current = {
    audio,
    cut: () => {
      audio.pause();
      settle("cut");
    },
  };
  audio.addEventListener("ended", () => settle("ended"), { once: true });
  audio.addEventListener("error", () => settle("failed"), { once: true });
  audio.play().catch(() => settle("failed"));

  const outcome = await finished;
  if (current?.audio === audio) current = null;
  if (outcome === "failed") speak(word, accentLang(accent));
}
